import { containsWholeWordPhrase, foldForMatching, removePhrases } from './reserved-text-matching';

/**
 * Re-exported for the composition role lexicon (and the pastry facts adapter
 * built on it): every text-scanning lexicon must share this exact folding and
 * word-boundary behaviour, and `composition-roles-lexicon.ts` reuses it from
 * here so the scan infra has a single entry point.
 */
export { containsWholeWordPhrase, foldForMatching, removePhrases } from './reserved-text-matching';

/**
 * The special-regime terms Reg. (UE) 1169/2011's plain scanner
 * (`nutrition-claim-lexicon.ts`) does not cover: biologico, DOP/IGP/STG,
 * senza glutine. Cluster C, gap G4.
 *
 * One lexicon, not three, because every entry carries the same shape: a term,
 * its variants, the locutions that are NOT a claim of it, and — the point —
 * the **condition that authorises its use**, typed. The condition's `kind` is
 * what decides which question the evaluator asks and which verdict class the
 * answer produces:
 *
 * - `CERTIFICATION` — the tenant is a certified operator (biologico, artt.
 *   34-35 Reg. (UE) 2018/848). The title is the OPERATOR's, not the
 *   product's.
 * - `ANALYTICAL_THRESHOLD` — an analysis shows a tenore below a threshold in
 *   mg/kg, on the food as sold (senza glutine, Reg. di esecuzione (UE)
 *   828/2014).
 * - `SPECIFICATION` — a registered disciplinare exists and the operator is
 *   inside its control system (DOP/IGP/STG, Reg. (UE) 2024/1143). Nothing the
 *   module holds can verify this, so a rule that reads it always routes to a
 *   specialist — never a data-entry task.
 */
export const RESERVED_TERM_REGIMES = ['ORGANIC', 'GLUTEN', 'GEOGRAPHICAL_INDICATION'] as const;
export type ReservedTermRegime = (typeof RESERVED_TERM_REGIMES)[number];

export type ReservedTermCondition =
  | { kind: 'CERTIFICATION'; regime: 'ORGANIC' }
  | { kind: 'ANALYTICAL_THRESHOLD'; analyte: 'GLUTEN'; maxMgPerKg: number }
  | { kind: 'SPECIFICATION'; scheme: 'PDO' | 'PGI' | 'TSG' };

export interface ReservedTerm {
  /** Stable key, e.g. 'organic', 'gluten-free'. */
  id: string;
  regime: ReservedTermRegime;
  /** Canonical form shown to the user. */
  term: string;
  /** Inflections, abbreviations and other-language forms to catch. Already lowercase. */
  variants: readonly string[];
  /** Locutions in which the term is NOT the reserved term. */
  exceptions: readonly string[];
  condition: ReservedTermCondition;
  legalReference: string;
  /** URL of the official source (EUR-Lex / Normattiva / Gazzetta Ufficiale). */
  sourceUrl: string;
}

const EU_2018_848_ART30_URL =
  'https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:02018R0848-20250325';
const EU_828_2014_URL = 'https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32014R0828';
const EU_2024_1143_URL =
  'https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:02024R1143-20260318';

/**
 * Art. 30 §1 Reg. (UE) 2018/848 reserves the Annex IV term "in every Union
 * language", not only in Italian: "organic" (EN) and "ecológico"/"orgánico"
 * (ES) on an Italian label are exactly as reserved as "biologico". "bio" and
 * "eco" are the abbreviations the article names explicitly.
 */
const ORGANIC_TERM: ReservedTerm = {
  id: 'organic',
  regime: 'ORGANIC',
  term: 'biologico',
  variants: [
    'biologico',
    'biologica',
    'biologici',
    'biologiche',
    'bio',
    'eco',
    'ecologico',
    'ecologica',
    'organic',
    'organico',
    'organica',
    'orgánico',
    'ecológico',
    'biologisch',
    'ökologisch',
    'biologique',
    'ekologisk',
    'luonnonmukainen',
  ],
  exceptions: [
    'valore biologico',
    'alto valore biologico',
    'orologio biologico',
    'ciclo biologico',
    'rischio biologico',
  ],
  condition: { kind: 'CERTIFICATION', regime: 'ORGANIC' },
  legalReference: 'Reg. (UE) 2018/848 art. 30 §1',
  sourceUrl: EU_2018_848_ART30_URL,
};

/** Art. 30 §3: the label may claim conversion only with this exact wording. */
const IN_CONVERSION_TERM: ReservedTerm = {
  id: 'in-conversion',
  regime: 'ORGANIC',
  term: 'in conversione',
  variants: ['in conversione', 'in-conversione', 'in conversion'],
  exceptions: [],
  condition: { kind: 'CERTIFICATION', regime: 'ORGANIC' },
  legalReference: 'Reg. (UE) 2018/848 art. 30 §3',
  sourceUrl: EU_2018_848_ART30_URL,
};

const GLUTEN_FREE_TERM: ReservedTerm = {
  id: 'gluten-free',
  regime: 'GLUTEN',
  term: 'senza glutine',
  variants: ['senza glutine', 'privo di glutine', 'gluten free', 'gluten-free'],
  exceptions: [],
  condition: { kind: 'ANALYTICAL_THRESHOLD', analyte: 'GLUTEN', maxMgPerKg: 20 },
  legalReference: 'Reg. di esecuzione (UE) 828/2014, allegato, sezione A',
  sourceUrl: EU_828_2014_URL,
};

const VERY_LOW_GLUTEN_TERM: ReservedTerm = {
  id: 'very-low-gluten',
  regime: 'GLUTEN',
  term: 'con contenuto di glutine molto basso',
  variants: [
    'con contenuto di glutine molto basso',
    'contenuto di glutine molto basso',
    'very low gluten',
  ],
  exceptions: [],
  condition: { kind: 'ANALYTICAL_THRESHOLD', analyte: 'GLUTEN', maxMgPerKg: 100 },
  legalReference: 'Reg. di esecuzione (UE) 828/2014, allegato, sezione A',
  sourceUrl: EU_828_2014_URL,
};

/**
 * Art. 3 §2: "adatto ai celiaci" is admitted only AS AN ACCOMPANIMENT to one
 * of the two wordings above — the same evidence the evaluator asks for
 * `gluten-free`, but the rule treats a standalone use of this wording apart
 * (see `EU.828_2014.GLUTEN_CLAIM` in the evaluator).
 */
const COELIAC_SUITABLE_TERM: ReservedTerm = {
  id: 'coeliac-suitable',
  regime: 'GLUTEN',
  term: 'adatto ai celiaci',
  variants: [
    'adatto ai celiaci',
    'adatto alle persone intolleranti al glutine',
    'specificamente formulato per celiaci',
    'specificamente formulato per persone intolleranti al glutine',
  ],
  exceptions: [],
  condition: { kind: 'ANALYTICAL_THRESHOLD', analyte: 'GLUTEN', maxMgPerKg: 20 },
  legalReference: 'Reg. di esecuzione (UE) 828/2014 art. 3 §2',
  sourceUrl: EU_828_2014_URL,
};

const PDO_TERM: ReservedTerm = {
  id: 'pdo',
  regime: 'GEOGRAPHICAL_INDICATION',
  term: 'DOP',
  variants: [
    'dop',
    'd.o.p.',
    'denominazione di origine protetta',
    'protected designation of origin',
  ],
  exceptions: [],
  condition: { kind: 'SPECIFICATION', scheme: 'PDO' },
  legalReference: 'Reg. (UE) 2024/1143 art. 26 §1',
  sourceUrl: EU_2024_1143_URL,
};

const PGI_TERM: ReservedTerm = {
  id: 'pgi',
  regime: 'GEOGRAPHICAL_INDICATION',
  term: 'IGP',
  variants: [
    'igp',
    'i.g.p.',
    'indicazione geografica protetta',
    'protected geographical indication',
  ],
  exceptions: [],
  condition: { kind: 'SPECIFICATION', scheme: 'PGI' },
  legalReference: 'Reg. (UE) 2024/1143 art. 26 §1',
  sourceUrl: EU_2024_1143_URL,
};

const TSG_TERM: ReservedTerm = {
  id: 'tsg',
  regime: 'GEOGRAPHICAL_INDICATION',
  term: 'STG',
  variants: [
    'stg',
    's.t.g.',
    'specialità tradizionale garantita',
    'traditional speciality guaranteed',
  ],
  exceptions: [],
  condition: { kind: 'SPECIFICATION', scheme: 'TSG' },
  legalReference: 'Reg. (UE) 2024/1143 art. 68 §1',
  sourceUrl: EU_2024_1143_URL,
};

export const RESERVED_TERMS: readonly ReservedTerm[] = [
  ORGANIC_TERM,
  IN_CONVERSION_TERM,
  GLUTEN_FREE_TERM,
  VERY_LOW_GLUTEN_TERM,
  COELIAC_SUITABLE_TERM,
  PDO_TERM,
  PGI_TERM,
  TSG_TERM,
];

export interface ReservedTermMatch {
  term: ReservedTerm;
  /** The variant actually found, so the caller can show it to the user. */
  matched: string;
}

function firstMatchOf(haystack: string, term: ReservedTerm): ReservedTermMatch | null {
  const withoutExceptions = removePhrases(
    haystack,
    term.exceptions.map((exception) => foldForMatching(exception)),
  );
  const matchedVariant = term.variants.find((variant) =>
    containsWholeWordPhrase(withoutExceptions, foldForMatching(variant)),
  );
  return matchedVariant === undefined ? null : { term, matched: matchedVariant };
}

/** Every reserved term found across `texts`, each reported once, lexicon order. */
export function findReservedTerms(texts: readonly string[]): ReservedTermMatch[] {
  const haystack = texts.map(foldForMatching).join(' \n ');
  const matches: ReservedTermMatch[] = [];
  for (const term of RESERVED_TERMS) {
    const match = firstMatchOf(haystack, term);
    if (match !== null) {
      matches.push(match);
    }
  }
  return matches;
}

/** Convenience for callers that only care about one regime (evaluator, UI). */
export function findReservedTermsOfRegime(
  texts: readonly string[],
  regime: ReservedTermRegime,
): ReservedTermMatch[] {
  return findReservedTerms(texts).filter((match) => match.term.regime === regime);
}
